import
{
    useLoadingIndicator
} from "../Loading";

import
{
    useWebsocket
} from './ReactDataProvider';

import
{
    useAlert
} from "../Dialog";

import type {
    RpcCreateSystemShareRequest,
    RpcCreateSystemShareResponse,
    RpcSendListRequest,
    RpcSendListResponse,
    RpcSystemUsersPushMessageRequest,
    RpcSystemUsersPushMessageResponse,
    RpcTestPushRequest,
    RpcTestPushResponse,
    RpcToggleCollectiveInvoiceRequest,
    RpcToggleCollectiveInvoiceResponse,
    RpcUpdateSystemShareOperateRequest,
    RpcUpdateSystemShareOperateResponse,
    RpcCreateSystemSetupRequest,
    RpcCreateSystemSetupResponse,
    RpcUpdatePlanStyleRequest,
    RpcUpdatePlanStyleResponse,
    RpcLogoutUserRequest,
    RpcLogoutUserResponse,
} from "@ews/react-data";
import
{
    useL10n
} from "@ews/react-localization-context";
import { useCallback } from "react";

import
{
    isEndUserMessage,
    RpcName
} from "@ews/react-data";


export type RpcFn<RequestT, ResponseT> = (request: RequestT) => Promise<ResponseT | null>;

export function mapRecord<K extends string, V, R>(
    obj: Record<K, V>,
    fn: (value: V, key: K) => R
): Record<K, R>
{
    const out = {} as Record<K, R>;
    for (const key in obj) {
        out[key] = fn(obj[key], key);
    }
    return out;
}

export const useInvokeRpc = <RequestT, ResponseT>(rpcName: string): RpcFn<RequestT, ResponseT> =>
{
    const loadingToggle = useLoadingIndicator();
    const alert = useAlert();
    const { translate: t } = useL10n();
    const provider = useWebsocket();

    const invoke = useCallback(async (data: RequestT) =>
    {
        try {
            const response: ResponseT = await provider.rpc(rpcName, data, loadingToggle, 300_000);
            return response;
        } catch (e: any) {
            console.error("invoke rpc: invocation failed", e);
            let message: string = "[REACT_RPC_INVOCATION_FAILED]";
            if (isEndUserMessage(e)) {
                // t does not translate the substitutions so we do it here
                const translatedSubstitutions = mapRecord(e.substitute ?? {}, (text) => t(`${text}`));
                message = t(e.translationKey, translatedSubstitutions);
                console.info("invoke rpc: error contains an end user message", message);
            }
            else {
                console.warn("invoke rpc: error does not contain an end user message, use default");
            }

            alert({
                "title": t('[REACT_RPC_INVOCATION_FAILED_TITLE]'),
                message,
                "buttonTitle": t('Dismiss')
            });
            return null;
        }
    }, [rpcName, loadingToggle, alert, t, provider]); // PERFORMANCE BUG: loadingToggle and alert are not stable so this will run every time

    return invoke;
};

export const useInvokeToggleCollectiveInvoice = () =>
{
    return useInvokeRpc<RpcToggleCollectiveInvoiceRequest, RpcToggleCollectiveInvoiceResponse>(RpcName.ToggleCollectiveInvoice);
};

export const useInvokeUpdateSystemShareOperate = () =>
{
    return useInvokeRpc<RpcUpdateSystemShareOperateRequest, RpcUpdateSystemShareOperateResponse>(RpcName.UpdateSystemShareOperate);
};

export const useInvokeSendList = () =>
{
    return useInvokeRpc<RpcSendListRequest, RpcSendListResponse>(RpcName.SendList);
};

export const useInvokeTestPush = () =>
{
    return useInvokeRpc<RpcTestPushRequest, RpcTestPushResponse>(RpcName.TestPush);
};

export const useInvokeInfoPush = () =>
{
    return useInvokeRpc<RpcSystemUsersPushMessageRequest, RpcSystemUsersPushMessageResponse>(RpcName.SystemUsersPush);
};
export const useInvokeCreateSystemShare = () =>
{
    return useInvokeRpc<RpcCreateSystemShareRequest, RpcCreateSystemShareResponse>(RpcName.CreateSystemShare);
};

export const useInvokeCreateSystemSetup = () =>
{
    return useInvokeRpc<RpcCreateSystemSetupRequest, RpcCreateSystemSetupResponse>(RpcName.CreateSystemSetup);
};

export const useInvokeUpdatePlanStyle = () =>
{
    return useInvokeRpc<RpcUpdatePlanStyleRequest, RpcUpdatePlanStyleResponse>(RpcName.UpdatePlanStyle);
};

export const useInvokeLogoutUser = () =>
{
    return useInvokeRpc<RpcLogoutUserRequest, RpcLogoutUserResponse>(RpcName.LogoutUser);
};
